<template>
  <div
    ref="container"
    class="sankey-container"
  >
    <svg
      v-if="nodes.length > 0"
      :width="width"
      :height="height"
    >
      <defs>
        <linearGradient
          v-for="link in links"
          :id="`grad-${link.id}`"
          :key="`grad-${link.id}`"
          gradientUnits="userSpaceOnUse"
          :x1="link.x0"
          y1="0"
          :x2="link.x1"
          y2="0"
        >
          <stop
            offset="0%"
            :stop-color="link.sourceColor"
          />
          <stop
            offset="100%"
            :stop-color="link.targetColor"
          />
        </linearGradient>
      </defs>

      <!-- Links -->
      <g>
        <path
          v-for="link in links"
          :key="link.id"
          :d="link.d"
          :stroke="`url(#grad-${link.id})`"
          :stroke-width="Math.max(link.width, 1)"
          fill="none"
          class="sankey-link"
          :data-tooltip="getTooltip(link)"
        >
          <title>{{ getTooltip(link) }}</title>
        </path>
      </g>

      <!-- Nodes -->
      <g>
        <rect
          v-for="node in nodes"
          :key="`node-${node.name}-${node.type}`"
          :x="node.x"
          :y="node.y"
          :width="nodeWidth"
          :height="Math.max(node.height, 2)"
          :fill="node.color"
          rx="4"
          class="sankey-node"
        >
          <title>{{ node.name }}: {{ formatValue(node.value) }} ({{ node.count }} calls)</title>
        </rect>
      </g>

      <!-- Labels -->
      <g>
        <text
          v-for="node in nodes"
          :key="`label-${node.name}-${node.type}`"
          :x="node.type === 'source' ? node.x + nodeWidth + 5 : node.x - 5"
          :y="node.y + node.height / 2"
          :text-anchor="node.type === 'source' ? 'start' : 'end'"
          dy="0.35em"
          class="sankey-label"
          :style="{ fill: textColor }"
        >
          {{ node.name }} ({{ node.count }})
        </text>
      </g>
    </svg>
    <div
      v-else
      class="text-center text-muted p-4"
    >
      No data available for flow visualization
    </div>
  </div>
</template>

<script>
export default {
  name: 'SankeyChart',
  props: {
    data: {
      type: Array,
      required: true
      // Expected format: [{ source: 'NameA', target: 'NameB', value: 100 }, ...]
    },
    nodeWidth: {
      type: Number,
      default: 20
    },
    padding: {
      type: Number,
      default: 20
    },
    currency: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      width: 0,
      height: 300, // Default height
      resizeObserver: null
    }
  },
  computed: {
    textColor() {
      // Basic detection for dark/light mode could go here, defaulting to gray-700
      return '#4a5568'
    },
    processedData() {
      if (!this.data || this.data.length === 0) {
return { sourceNodes: [], targetNodes: [], links: [] }
}

      // 1. aggregations
      const sources = {}
      const targets = {}
      const links = []

      this.data.forEach((item, index) => {
        if (!sources[item.source]) {
            sources[item.source] = { name: item.source, value: 0, count: 0, type: 'source' }
        }

        if (!targets[item.target]) {
            targets[item.target] = { name: item.target, value: 0, count: 0, type: 'target' }
        }
        
        sources[item.source].value += item.value
        sources[item.source].count += (item.count || 0)
        
        targets[item.target].value += item.value
        targets[item.target].count += (item.count || 0)
        
        links.push({
          id: index,
          sourceName: item.source,
          targetName: item.target,
          value: item.value,
          count: item.count || 0
        })
      })

      // Convert to arrays and sort by value descending
      const useCount = Object.values(sources).reduce((acc, s) => acc + s.value, 0) === 0
      
      const sourceNodes = Object.values(sources).sort((a, b) => useCount ? b.count - a.count : b.value - a.value)
      const targetNodes = Object.values(targets).sort((a, b) => useCount ? b.count - a.count : b.value - a.value)

      return { sourceNodes, targetNodes, links, useCount }
    },
    layout() {
      const { sourceNodes, targetNodes, links, useCount } = this.processedData
      if (sourceNodes.length === 0) {
        return { nodes: [], links: [] }
      }

      // Check total weight based on value or count
      const totalWeight = sourceNodes.reduce((sum, n) => sum + (useCount ? n.count : n.value), 0)
      if (totalWeight === 0) {
        return { nodes: [], links: [] }
      }

      // Layout constants
      const availableHeight = this.height - (this.padding * 2)
      
      const nodePadding = 15 // gap between nodes
      const sourceTotalPadding =  (sourceNodes.length - 1) * nodePadding
      const targetTotalPadding = (targetNodes.length - 1) * nodePadding
      
      const effectiveHeight = availableHeight - Math.max(sourceTotalPadding, targetTotalPadding)
      const scale = effectiveHeight / totalWeight

      // Assign positions to Nodes
      let currentY = this.padding
      const finalNodes = []

      // Source Nodes (Left)
      sourceNodes.forEach((node, i) => {
        node.height = (useCount ? node.count : node.value) * scale
        node.x = this.padding
        node.y = currentY
        node.color = this.getColor(i, 'source')
        node.currentY = 0 // for links processing
        finalNodes.push(node)
        currentY += node.height + nodePadding
      })

      // Target Nodes (Right)
      currentY = this.padding
      targetNodes.forEach((node, i) => {
        node.height = (useCount ? node.count : node.value) * scale
        node.x = this.width - this.padding - this.nodeWidth
        node.y = currentY
        node.color = this.getColor(i, 'target')
        node.currentY = 0 // for links processing
        finalNodes.push(node)
        currentY += node.height + nodePadding
      })

      // Process Links
      const finalLinks = links.map(link => {
        const source = sourceNodes.find(n => n.name === link.sourceName)
        const target = targetNodes.find(n => n.name === link.targetName)
        
        const linkHeight = (useCount ? link.count : link.value) * scale
        
        const y0 = source.y + source.currentY + linkHeight / 2
        const y1 = target.y + target.currentY + linkHeight / 2
        
        source.currentY += linkHeight
        target.currentY += linkHeight

        const x0 = source.x + this.nodeWidth
        const x1 = target.x
        
        // Calculate Bezier path
        const curvature = 0.5
        const xi = d3_interpolateNumber(x0, x1)
        const x2 = xi(curvature)
        const x3 = xi(1 - curvature)
        
        // SVG Path command for a "thick" line (we use stroke-width)
        // Simple cubic bezier: M x0 y0 C x2 y0, x3 y1, x1 y1
        const d = `M${x0},${y0} C${x2},${y0} ${x3},${y1} ${x1},${y1}`

        return {
          id: link.id,
          source,
          target,
          value: link.value,
          width: linkHeight,
          d,
          x0, 
          x1,
          sourceColor: source.color,
          targetColor: target.color,
          count: link.count,
          percentage: totalWeight > 0 ? ((useCount ? link.count : link.value) / totalWeight * 100).toFixed(1) : 0
        }
      })

      return { nodes: finalNodes, links: finalLinks }
    },
    nodes() {
      return this.layout.nodes
    },
    links() {
      return this.layout.links
    }
  },
  mounted() {
    this.updateDimensions()
    this.resizeObserver = new ResizeObserver(this.updateDimensions)
    this.resizeObserver.observe(this.$refs.container)
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  },
  methods: {
    updateDimensions() {
      if (this.$refs.container) {
        this.width = this.$refs.container.clientWidth
        // We could also adjust height based on number of nodes if needed
      }
    },
    getColor(index, type) {
      // Use more distinct, professional colors
      const sourceColors = ['#4299E1', '#3182CE', '#2B6CB0', '#63B3ED', '#90CDF4'] // Blues
      const targetColors = ['#48BB78', '#38A169', '#2F855A', '#68D391', '#9AE6B4', '#F6E05E', '#ECC94B', '#D69E2E'] // Greens & Yellows
      
      if (type === 'source') {
        return sourceColors[index % sourceColors.length]
      }
      return targetColors[index % targetColors.length]
    },
    formatValue(value) {
      if (this.currency) {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
      }
      return value.toLocaleString()
    },
    getTooltip(link) {
        const val = this.formatValue(link.value)
        return `${link.source.name} → ${link.target.name}\nAmount: ${val} (${link.percentage}%)\nCalls: ${link.count}`
    }
  }
}

// Helper for interpolation
function d3_interpolateNumber(a, b) {
  return function(t) {
    return a * (1 - t) + b * t
  }
}
</script>

<style scoped>
.sankey-container {
  width: 100%;
  height: 100%;
  min-height: 300px;
}
.sankey-link {
  opacity: 0.4;
  transition: opacity 0.3s;
}
.sankey-link:hover {
  opacity: 0.7;
}
.sankey-node {
  stroke: #fff;
  stroke-width: 1px;
}
.sankey-label {
  font-size: 11px;
  font-family: inherit;
  font-weight: 500;
  pointer-events: none;
  text-shadow: 0 1px 0 #fff;
}
</style>
